'use client';

import { useRef, useEffect } from 'react';

/**
 * Componente para la sección "Modelos y Herramientas de IA"
 * 
 * En desktop: Muestra las 3 cards en un grid (TEXTO, IMAGEN, OPEN SOURCE/WEB)
 * En mobile: Muestra un carrusel horizontal que permite deslizar entre las 3 cards,
 * mostrando 2 cards a la vez.
 * 
 * Cards:
 * 1. TEXTO - Modelos de texto (GPT, Claude, Gemini, Mistral, Grok)
 * 2. IMAGEN - Modelos de imagen (Nano Banana, FLUX, GPT Image, Recraft, Stable Diffusion)
 * 3. OPEN SOURCE/WEB - Combinación de dos sub-cards (DeepSeek, Llama, Perplexity, OpenPerplex)
 */
export default function ModelsToolsSection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Datos de las cards
  const textoModels = [
    { name: 'GPT', icon: '/openai.svg', price: 'usd 20/mes' },
    { name: 'Claude', icon: '/claude.png', price: 'usd 20/mes' },
    { name: 'Gemini', icon: '/gemini-2025.png', price: 'usd 20/mes' },
    { name: 'Mistral', icon: '/mistral.png', price: 'usd 15/mes' },
    { name: 'Grok', icon: '/grok.svg', price: 'usd 30/mes' },
  ];

  const imagenModels = [
    { name: 'Nano Banana', icon: '/gemini-2025.png', price: 'usd 20/mes' },
    { name: 'FLUX', icon: '/black-forest-labs.svg', price: 'usd 10/mes' },
    { name: 'GPT Image', icon: '/openai.svg', price: 'usd 20/mes' },
    { name: 'Recraft', icon: '/recraft.svg', price: 'usd 10/mes' },
    { name: 'Stable Diffusion', icon: '/stability-ai.png', price: 'usd 20/mes' },
  ];

  const openSourceModels = [
    { name: 'DeepSeek', icon: '/deepseek.png', price: 'usd variable' },
    { name: 'Llama', icon: '/meta.png', price: 'usd variable' },
  ];

  const webModels = [
    { name: 'Perplexity', icon: '/perplexity.png', price: 'usd 20/mes' },
    { name: 'OpenPerplex', icon: '/openperplex.png', price: 'Para desarrolladores' },
  ];

  // SVG para las líneas conectivas (desktop)
  const desktopConnectorSvg = "data:image/svg+xml,%3csvg%20width='556'%20height='148'%20viewBox='0%200%20556%20148'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20d='M554.682%200C555.134%200%20555.5%200.366516%20555.5%200.818359V35.8359C555.5%2054.2912%20540.404%2069.3875%20521.948%2069.3877H310.733C293.182%2069.3879%20278.819%2083.7514%20278.818%20101.303V136.394H283.754C283.876%20136.394%20283.958%20136.525%20283.9%20136.631L278.14%20147.646C278.082%20147.76%20277.911%20147.76%20277.854%20147.646L272.092%20136.631C272.035%20136.525%20272.117%20136.394%20272.239%20136.394H277.182V101.377C277.182%2083.8255%20262.818%2069.4611%20245.267%2069.4609H34.0518C15.5964%2069.4608%200.500066%2054.3645%200.5%2035.9092V0.892578C0.5%200.440619%200.866401%200.0742188%201.31836%200.0742188C1.77031%200.0742273%202.13672%200.440625%202.13672%200.892578V35.9092C2.13678%2053.4606%2016.5003%2067.824%2034.0518%2067.8242H245.267C260.124%2067.8244%20272.802%2077.6091%20277.182%2091.0527V0.892578C277.182%200.440624%20277.548%200.0742267%20278%200.0742188C278.452%200.0742188%20278.818%200.440619%20278.818%200.892578V90.9795C283.198%2077.5357%20295.876%2067.7511%20310.733%2067.751H521.948C539.5%2067.7508%20553.863%2053.3872%20553.863%2035.8359V0.818359C553.863%200.366631%20554.23%200.000185444%20554.682%200Z'%20fill='%2399735F'/%3e%3c/svg%3e";

  // SVG para las líneas conectivas (mobile)
  const mobileConnectorSvg = "data:image/svg+xml,%3csvg%20width='146'%20height='105'%20viewBox='0%200%20146%20105'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20d='M72.1172%2081.0501V76.4992C72.1172%2057.2788%2087.8394%2041.5567%20107.06%2041.5567L111.027%2041.5566C129.306%2041.5566%20144.265%2042.2789%20144.265%2024L144.265%200.852257C144.265%200.381681%20144.647%200.000182678%20145.117%200C145.588%200%20145.97%200.381568%20145.97%200.852257L145.97%2024C145.97%2043.2203%20130.248%2043.2612%20111.027%2043.2612L107.06%2043.2612C88.7808%2043.2612%2073.8217%2058.2202%2073.8217%2076.4992V81.0501C73.8217%2081.5208%2073.4401%2081.9024%2072.9694%2081.9024C72.4988%2081.9024%2072.1172%2081.5208%2072.1172%2081.0501Z'%20fill='%2399735F'/%3e%3cpath%20d='M73.8789%2095.5V76.4992C73.8789%2057.2788%2058.1567%2041.5567%2038.9364%2041.5567L34.9688%2041.5566C16.6898%2041.5566%201.73073%2042.2789%201.73071%2024L1.7309%200.852257C1.7309%200.381681%201.3492%200.000182678%200.878662%200C0.407974%200%200.0263824%200.381568%200.0263824%200.852257L0.0262146%2024C0.0262299%2043.2203%2015.7484%2043.2612%2034.9688%2043.2612L38.9364%2043.2612C57.2153%2043.2612%2072.1744%2058.2202%2072.1744%2076.4992V95.5C72.1744%2095.9707%2072.556%2096.3523%2073.0266%2096.3523C73.4973%2096.3523%2073.8789%2095.9707%2073.8789%2095.5Z'%20fill='%2399735F'/%3e%3cpath%20d='M78.9734%2092.25H66.9736C66.8458%2092.25%2066.7606%2092.3864%2066.8202%2092.4972L72.8201%20103.969C72.8798%20104.088%2073.0587%20104.088%2073.1184%20103.969L79.1183%2092.4972C79.1779%2092.3864%2079.0927%2092.25%2078.9649%2092.25H78.9734Z'%20fill='%2399735F'/%3e%3c/svg%3e";

  // Componente para renderizar una card individual
  const ModelCard = ({ 
    title, 
    models, 
    className = '' 
  }: { 
    title: string; 
    models: Array<{ name: string; icon: string; price: string }>; 
    className?: string;
  }) => (
    <div className={`p-1 md:p-5 border border-dashed border-gray-400 rounded-xl flex flex-col items-center gap-2 md:gap-4 ${className}`}>
      <div className="text-center text-xs sm:text-sm md:text-sm text-gray-400 font-semibold uppercase">
        {title}
      </div>
      <div className="w-full h-full flex flex-col justify-between gap-2">
        {models.map((model, index) => (
          <div
            key={index}
            className="w-full h-full px-2 sm:px-2.5 md:px-3 py-1.5 sm:py-2 md:py-1.5 bg-white border border-gray-300 rounded-md md:rounded-lg flex gap-1.5 sm:gap-2 md:gap-4 items-center min-h-[44px]"
          >
            <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7">
              <img alt={model.name} className="w-full h-full" src={model.icon} />
            </div>
            <div className="h-full flex flex-col justify-center">
              <div className="text-xs sm:text-sm md:text-base">{model.name}</div>
              <div className="text-[10px] sm:text-xs md:text-sm text-text2">{model.price}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Componente para la card combinada OPEN SOURCE/WEB (solo para mobile carousel)
  const CombinedOpenSourceWebCard = () => (
    <div className="p-1 md:p-5 border border-dashed border-gray-400 rounded-xl flex flex-col items-center gap-2 md:gap-4">
      {/* OPEN SOURCE */}
      <div className="w-full">
        <div className="text-center text-xs sm:text-sm md:text-sm text-gray-400 font-semibold uppercase mb-2">
          OPEN SOURCE
        </div>
        <div className="w-full h-full flex flex-col justify-between gap-2">
          {openSourceModels.map((model, index) => (
            <div
              key={`opensource-${index}`}
              className="w-full h-full px-2 sm:px-2.5 md:px-3 py-1.5 sm:py-2 md:py-1.5 bg-white border border-gray-300 rounded-md md:rounded-lg flex gap-1.5 sm:gap-2 md:gap-4 items-center min-h-[44px]"
            >
              <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7">
                <img alt={model.name} className="w-full h-full" src={model.icon} />
              </div>
              <div className="h-full flex flex-col justify-center">
                <div className="text-xs sm:text-sm md:text-base">{model.name}</div>
                <div className="text-[10px] sm:text-xs md:text-sm text-text2">{model.price}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* WEB */}
      <div className="w-full mt-2">
        <div className="text-center text-xs sm:text-sm md:text-sm text-gray-400 font-semibold uppercase mb-2">
          WEB
        </div>
        <div className="w-full h-full flex flex-col justify-between gap-2">
          {webModels.map((model, index) => (
            <div
              key={`web-${index}`}
              className="w-full h-full px-2 sm:px-2.5 md:px-3 py-1.5 sm:py-2 md:py-1.5 bg-white border border-gray-300 rounded-md md:rounded-lg flex gap-1.5 sm:gap-2 md:gap-4 items-center min-h-[44px]"
            >
              <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7">
                <img alt={model.name} className="w-full h-full" src={model.icon} />
              </div>
              <div className="h-full flex flex-col justify-center">
                <div className="text-xs sm:text-sm md:text-base">{model.name}</div>
                <div className="text-[10px] sm:text-xs md:text-sm text-text2">{model.price}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full py-8 flex flex-col gap-8 md:gap-16 items-center justify-center mx-auto pt-14 pb-24 max-w-[1000px]">
      {/* Títulos */}
      <div className="w-full flex flex-col gap-3 items-center">
        <h2 className="font-semibold text-2xl sm:text-3xl md:text-4xl text-center text-darkbrown px-2">
          Modelos y Herramientas de IA
        </h2>
        <h3 className="text-lg sm:text-xl md:text-3xl text-center text-text2 px-2">
          Lideres en el mercado y sus precios de referencia
        </h3>
      </div>

      <div className="w-full flex flex-col gap-3 sm:gap-6 items-center justify-center">
        {/* Desktop: Grid con las 3 cards */}
        <div className="flex flex-col items-center w-full max-sm:hidden">
          <div className="w-full grid grid-cols-3 grid-rows-2 gap-2 md:gap-4">
            {/* Card 1: TEXTO */}
            <ModelCard 
              title="TEXTO" 
              models={textoModels} 
              className="row-span-2" 
            />
            
            {/* Card 2: IMAGEN */}
            <ModelCard 
              title="IMAGEN" 
              models={imagenModels} 
              className="row-span-2" 
            />
            
            {/* Card 3: OPEN SOURCE */}
            <ModelCard 
              title="OPEN SOURCE" 
              models={openSourceModels} 
              className="" 
            />
            
            {/* Card 4: WEB */}
            <ModelCard 
              title="WEB" 
              models={webModels} 
              className="col-start-3 row-start-2" 
            />
          </div>
          
          {/* Líneas conectivas desktop */}
          <img 
            className="w-8/12 mt-4" 
            src={desktopConnectorSvg} 
            alt="Connector lines" 
          />
          
          {/* AbbiAI Card */}
          <div className="w-auto px-2 md:px-5 py-1.5 md:py-3 bg-white border border-gray-300 rounded-md md:rounded-lg flex gap-1 md:gap-4 items-center mt-4">
            <div className="flex-shrink-0 w-9 h-9 md:w-12 md:h-12">
              <div className="h-full">
                <img src="/AbbiAI.png" alt="AbbiAI Logo" className="h-full w-auto object-contain" />
              </div>
            </div>
            <div className="h-full flex flex-col justify-center">
              <div className="text-2xl max-md:text-lg max-md:leading-4 font-bold text-darkbrown dark:text-white">
                AbbiAI
              </div>
              <div className="text-green-500 max-md:text-xs">usd 10/mes</div>
            </div>
          </div>
        </div>

        {/* Mobile: Carrusel horizontal con 2 cards visibles */}
        <div className="sm:hidden w-full max-w-[330px]">
          <div 
            ref={scrollContainerRef}
            className="relative w-full overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <div className="flex gap-4" style={{ width: 'max-content' }}>
              {/* Card 1: TEXTO - Ancho fijo para mostrar 2 cards a la vez */}
              <div className="flex flex-none snap-center" style={{ width: '157px' }}>
                <ModelCard title="TEXTO" models={textoModels} className="w-full" />
              </div>
              
              {/* Card 2: IMAGEN - Ancho fijo para mostrar 2 cards a la vez */}
              <div className="flex flex-none snap-center" style={{ width: '157px' }}>
                <ModelCard title="IMAGEN" models={imagenModels} className="w-full" />
              </div>
              
              {/* Card 3: OPEN SOURCE/WEB (combinada) - Ancho fijo para mostrar 2 cards a la vez */}
              <div className="flex flex-none snap-center" style={{ width: '157px' }}>
                <CombinedOpenSourceWebCard />
              </div>
            </div>
          </div>
          
          {/* Líneas conectivas mobile */}
          <img 
            className="w-6/12 h-1/2 mx-auto mt-4" 
            src={mobileConnectorSvg} 
            alt="Connector lines" 
          />
          
          {/* AbbiAI Card */}
          <div className="w-auto px-2 md:px-5 py-1.5 md:py-3 bg-white border border-gray-300 rounded-md md:rounded-lg flex gap-1 md:gap-4 items-center mx-auto mt-4">
            <div className="flex-shrink-0 w-9 h-9 md:w-12 md:h-12">
              <div className="h-full">
                <img src="/AbbiAI.png" alt="AbbiAI Logo" className="h-full w-auto object-contain" />
              </div>
            </div>
            <div className="h-full flex flex-col justify-center">
              <div className="text-2xl max-md:text-lg max-md:leading-4 font-bold text-darkbrown dark:text-white">
                AbbiAI
              </div>
              <div className="text-green-500 max-md:text-xs">usd 10/mes</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

